import React, { useState, useEffect } from 'react';
import NavContainer from './styles';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useProductsContext } from '../../context/products_context';
import { useCartContext } from '../../context/cart_context';
import { useUserContext } from '../../context/user_context';
import { useLanguageContext } from '../../context/language_context';
import { useFilterContext } from '../../context/filter_context';
import { useWishlistContext } from '../../context/wishlist_context';
import { default_profile_image, categories_url } from '../../utils/constants';
import { getSecureUrl } from '../../utils/helpers';
import { ShoppingBag, User, ClipboardList, Search, Heart } from 'lucide-react';
import axios from 'axios';
import daristaGoldLogo from '../../assets/daristaGoldLogoPM.png';
import daristaWhiteLogo from '../../assets/DaristaWhiteLogo.png';
import SearchBar from './SearchBar';

const Nav = () => {
  const { currentUser } = useUserContext();
  const { openSidebar, isSidebarOpen } = useProductsContext();
  const { total_items } = useCartContext();
  const { setCategoryFilter } = useFilterContext();
  const { t, isArabic, language, toggleLanguage } = useLanguageContext();
  const { wishlist } = useWishlistContext();
  const location = useLocation();
  const history = useHistory();
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(categories_url);
        const cats = (response.data.data || []).map((cat) => ({
          ...cat,
          image: cat.image ? { ...cat.image, url: getSecureUrl(cat.image.url) } : cat.image,
        }));
        setCategories(cats.slice(0, 6)); // Limit to 6 categories in navbar
      } catch (err) {
        // silently ignore
      }
    };
    fetchCategories();
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategoryClick = (category) => {
    // Use URL params so ProductsPage can read and apply the filter
    history.push(`/products?category=${encodeURIComponent(category.toLowerCase())}`);
  };

  // White logo when at top (transparent navbar over olive hero), gold logo when scrolled (white navbar)
  const useWhiteLogo = !isScrolled;
  const isOliveTopPage = ['/wishlist', '/login', '/register'].includes(location.pathname);

  return (
    <NavContainer
      className={`${isScrolled ? 'scrolled' : ''} ${isSidebarOpen ? 'sidebar-open' : ''} ${isOliveTopPage && !isScrolled ? 'olive-top' : ''}`}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Top Promo Bar */}
      <div className="promo-bar">
        <p>
          {isArabic
            ? '✨ توصيل مجاني للطلبات فوق 200 دينار | الدفع عند الاستلام متاح'
            : '✨ Free delivery on orders over 200 TND | Cash on Delivery Available'}
        </p>
      </div>

      {/* Main Navigation */}
      <div className="nav-main">
        <div className="nav-center">
          {/* Mobile Menu Button */}
          <button type="button" className="nav-toggle" onClick={openSidebar}>
            <FaBars />
          </button>

          {/* Logo */}
          <div className="nav-logo">
            <Link to="/">
              <img
                src={useWhiteLogo ? daristaWhiteLogo : daristaGoldLogo}
                alt="Darista"
                className="nav-logo-img"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Categories */}
          <ul className="nav-links">
            {categories.map((category) => (
              <li key={category._id}>
                <button
                  className="nav-link"
                  onClick={() => handleCategoryClick(category.name)}
                >
                  {isArabic ? (category.name_ar || category.name) : category.name}
                </button>
              </li>
            ))}
            {categories.length > 0 && (
              <li>
                <Link
                  to="/offers"
                  className="nav-link highlight"
                >
                  {isArabic ? 'العروض' : 'Offers'}
                </Link>
              </li>
            )}
          </ul>

          {/* Right Icons */}
          <div className="nav-right">
            <div className="nav-icons">
              {/* Search */}
              <button
                className="icon-btn"
                onClick={() => setSearchOpen(!searchOpen)}
                aria-label="Search"
              >
                {searchOpen ? <FaTimes /> : <Search size={20} />}
              </button>

              {/* Language Toggle */}
              <button
                className="lang-toggle-nav"
                onClick={toggleLanguage}
                aria-label="Toggle language"
              >
                {language === 'ar' ? 'EN' : 'ع'}
              </button>

              {/* Wishlist */}
              <Link to="/wishlist" className="icon-btn" aria-label="Wishlist">
                <Heart size={20} />
                {wishlist.length > 0 && (
                  <span className="icon-badge">{wishlist.length}</span>
                )}
              </Link>

              {/* Cart */}
              <Link to="/cart" className="icon-btn" aria-label="Cart">
                <ShoppingBag size={20} />
                {total_items > 0 && (
                  <span className="icon-badge">{total_items}</span>
                )}
              </Link>

              {/* My Orders */}
              {currentUser && (
                <Link to="/orders" className="icon-btn" aria-label="My Orders">
                  <ClipboardList size={20} />
                </Link>
              )}

              {/* User/Profile */}
              {!currentUser ? (
                <Link to="/login" className="icon-btn" aria-label="Login">
                  <User size={20} />
                </Link>
              ) : (
                <Link to="/profile" className="profile-btn">
                  <img
                    src={currentUser.photoURL || default_profile_image}
                    alt="profile"
                  />
                </Link>
              )}
            </div>

            {/* Mobile Toggle - already above for mobile order */}
          </div>
        </div>
      </div>

      {/* Search Bar with Autocomplete */}
      <SearchBar isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </NavContainer>
  );
};

export default Nav;
